import { useMemo } from 'react';

import { useAddToBatch } from './action-type-definitions/use-add-to-batch';
import { useAdvancedSearchCancelOrder } from './action-type-definitions/use-advanced-search-cancel-order';
import { useAdvancedSearchCombine } from './action-type-definitions/use-advanced-search-combine';
import { useAdvancedSearchMarkShipped } from './action-type-definitions/use-advanced-search-mark-as-shipped';
import { useAutoBatching } from './action-type-definitions/use-auto-batching';
import { useCancelOrder } from './action-type-definitions/use-cancel-order';
import { useExportOrders } from './action-type-definitions/use-export-orders';
import { useImportOrders } from './action-type-definitions/use-import-orders';
import { useManagePresets } from './action-type-definitions/use-manage-presets';
import { useMarkShipped } from './action-type-definitions/use-mark-shipped';
import { useMarkShippedBulk } from './action-type-definitions/use-mark-shipped-bulk';
import { usePendingFulfillmentCancelOrder } from './action-type-definitions/use-pending-fulfillment-cancel-order';
import { useRefreshSelectedOrders } from './action-type-definitions/use-refresh-selected-orders';
import { useScanToSearch } from './action-type-definitions/use-scan-to-search';
import { useShipTogether } from './action-type-definitions/use-ship-together';
import { useSplitOrder } from './action-type-definitions/use-split-order';
import { useValidateAddress } from './action-type-definitions/use-validate-address';
import { useTopLevelOrdersViewId } from '../../pages/orders-page/orders-left-nav/orders-left-nav-context';

import {
  ADVANCED_SEARCH_ID,
  ALL_ORDERS_SEARCH_RESULT_ID,
  AWAITING_PAYMENT_ID,
  AWAITING_SHIPMENT_ID,
  CANCELLED_ID,
  ON_HOLD_ID,
  PENDING_FULFILLMENT_ID,
  REJECTED_FULFILLMENT_ID,
  SHIPPED_ID,
} from '../../pages/orders-page/orders-page-view-constants';

export const useOrderViewActionTypes = () => {
  const orderViewId = useTopLevelOrdersViewId();
  const addToBatch = useAddToBatch();
  const cancelOrder = useCancelOrder();
  const exportOrders = useExportOrders();
  const importOrders = useImportOrders();
  const managePresets = useManagePresets();
  const markShipped = useMarkShipped();
  const markShippedBulk = useMarkShippedBulk();
  const shipTogether = useShipTogether();
  const splitOrder = useSplitOrder();
  const validateAddress = useValidateAddress();
  const advancedSearchCancelOrder = useAdvancedSearchCancelOrder();
  const advancedSearchCombine = useAdvancedSearchCombine();
  const advancedSearchMarkShipped = useAdvancedSearchMarkShipped();
  const scanToSearch = useScanToSearch();
  const autoBatching = useAutoBatching();
  const pendingFulfillmentCancelOrder = usePendingFulfillmentCancelOrder();
  const refreshSelectedOrders = useRefreshSelectedOrders();

  // If autoBatching is disabled, we return an empty array to avoid rendering the action in the menu.
  const autoBatchingArray = !autoBatching.isDisabled ? [autoBatching] : [];
  const result = useMemo(() => {
    if (orderViewId === ALL_ORDERS_SEARCH_RESULT_ID) {
      return [
        cancelOrder,
        shipTogether,
        splitOrder,
        markShipped,
        markShippedBulk,
        refreshSelectedOrders,
        importOrders,
        exportOrders,
        addToBatch,
        scanToSearch,
        validateAddress,
        managePresets,
      ];
    }
    if (orderViewId === AWAITING_PAYMENT_ID) {
      return [
        cancelOrder,
        markShipped,
        markShippedBulk,
        refreshSelectedOrders,
        exportOrders,
        addToBatch,
        validateAddress,
        managePresets,
        ...autoBatchingArray,
      ];
    }
    if (orderViewId === ON_HOLD_ID) {
      return [
        cancelOrder,
        splitOrder,
        markShipped,
        markShippedBulk,
        refreshSelectedOrders,
        exportOrders,
        validateAddress,
        managePresets,
        ...autoBatchingArray,
      ];
    }
    if (orderViewId === AWAITING_SHIPMENT_ID) {
      return [
        cancelOrder,
        shipTogether,
        splitOrder,
        markShipped,
        markShippedBulk,
        refreshSelectedOrders,
        importOrders,
        exportOrders,
        addToBatch,
        scanToSearch,
        validateAddress,
        managePresets,
        ...autoBatchingArray,
      ];
    }
    if (orderViewId === PENDING_FULFILLMENT_ID) {
      return [pendingFulfillmentCancelOrder, importOrders, exportOrders, scanToSearch, ...autoBatchingArray];
    }
    if (orderViewId === REJECTED_FULFILLMENT_ID) {
      return [cancelOrder, importOrders, exportOrders, scanToSearch, ...autoBatchingArray];
    }
    if (orderViewId === SHIPPED_ID) {
      return [exportOrders, addToBatch, managePresets, scanToSearch, ...autoBatchingArray];
    }
    if (orderViewId === CANCELLED_ID) {
      return [exportOrders, addToBatch, managePresets, scanToSearch, ...autoBatchingArray];
    }
    if (orderViewId === ADVANCED_SEARCH_ID) {
      return [
        advancedSearchCancelOrder,
        advancedSearchCombine,
        advancedSearchMarkShipped,
        markShippedBulk,
        importOrders,
        exportOrders,
        addToBatch,
        scanToSearch,
        splitOrder,
        validateAddress,
        managePresets,
      ];
    }

    return [];
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addToBatch,
    advancedSearchCancelOrder,
    advancedSearchCombine,
    advancedSearchMarkShipped,
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    autoBatchingArray,
    cancelOrder,
    exportOrders,
    importOrders,
    markShipped,
    markShippedBulk,
    managePresets,
    orderViewId,
    refreshSelectedOrders,
    scanToSearch,
    shipTogether,
    splitOrder,
    validateAddress,
  ]);

  return result;
};
